import type { RosterScheduleDayCell } from "@/types/snapshot-type";

const WARD_COLORS = [
  "#0e59c6",
  "#0f766e",
  "#a16207",
  "#7c3aed",
  "#be123c",
  "#475569",
] as const;
export function wardIndicatorColor(groupId: string): string {
  let hash = 0;
  for (const letter of groupId) hash = (hash * 31 + letter.charCodeAt(0)) >>> 0;
  return WARD_COLORS[hash % WARD_COLORS.length];
}
/** Mobile ward palette, allocated once per displayed month's sorted ward IDs. */
export function wardIndicatorColorMap(
  groupIds: readonly string[]
): Map<string, string> {
  const colors = ["#0e59c6", "#7c3aed", "#be185d", "#047857", "#b45309"];
  const ids = [
    ...new Set(groupIds.map((id) => id.trim()).filter(Boolean)),
  ].sort();
  return new Map(
    ids.map((id, index) => [
      id,
      colors[index] ??
        `hsl(${(((index - colors.length) * 137.508) % 360).toFixed(3)} 65% 36%)`,
    ])
  );
}
export function assignmentReasonLabel(reason: string): string {
  const value = reason.trim();
  return value === "병동이동" || value === "부서이동" ? "병동 이동" : value;
}
export function issuedCellStatus(
  cell: RosterScheduleDayCell | undefined
): string | undefined {
  if (cell?.is_issued === false) return "미발행";
  switch (cell?.status) {
    case "target_not_issued":
    case "target_no_row":
      return "미발행";
    case "assignment_conflict":
      return "확인 필요";
    case "confirmed":
    case undefined:
      return undefined;
  }
}
export function issuedCellCode(cell: RosterScheduleDayCell): string {
  return issuedCellStatus(cell) === "미발행" ? "" : cell.code;
}
export function issuedCellAssignment(
  cell: RosterScheduleDayCell | undefined,
  activeGroupId: string
) {
  if (!cell) return null;
  const groupId = cell.target_group_id || cell.group_id || "";
  const reason =
    cell.reason === "병동이동" || cell.reason === "부서이동"
      ? "병동 이동"
      : cell.reason === "파견"
        ? "파견"
        : "";
  if (!groupId || groupId === activeGroupId || !reason) return null;
  const label = `${cell.target_group_name || cell.group_name || groupId} · ${reason}`;
  return {
    key: `${groupId}:${reason}`,
    groupId,
    label,
    color: wardIndicatorColor(groupId),
  };
}
export function issuedAssignmentBand(
  cells: readonly RosterScheduleDayCell[],
  index: number,
  activeGroupId: string,
  columns?: number,
  offset = 0
) {
  const today = issuedCellAssignment(cells[index], activeGroupId);
  if (!today) return null;
  const boundary = columns !== undefined && (offset + index) % columns === 0;
  if (
    index > 0 &&
    !boundary &&
    issuedCellAssignment(cells[index - 1], activeGroupId)?.key === today.key
  )
    return null;
  const limit =
    columns === undefined
      ? cells.length
      : Math.min(cells.length, index + columns - ((offset + index) % columns));
  let end = index + 1;
  while (
    end < limit &&
    issuedCellAssignment(cells[end], activeGroupId)?.key === today.key
  )
    end++;
  return { ...today, spanDays: end - index };
}
